import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Dashboard from './components/Dashboard';
import Admin from './components/Admin';
import Login from './components/Login';
import Signup from './components/Signup';
import Upgrade from './components/Upgrade';
import QuestionOfTheDay from './components/QuestionOfTheDay';
import QuestionOfTheDayMobile from './components/QuestionOfTheDayMobile';
import Query from './components/Query';
import Questions from './components/Questions';
import SearchAnswers from './components/SearchAnswers';
import AddQuestion from './components/AddQuestion';
import AccountSettings from './components/AccountSettings';
import ManageSubscription from './components/ManageSubscription';
import ErrorBoundary from './components/ErrorBoundary';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Unsubscribe from './components/Unsubscribe';
import Help from './components/Help';
import Contact from './components/Contact';
import Footer from './components/Footer';
import { API_URL } from './config';

function App() {
  const [renderError, setRenderError] = useState('');
  const [isAdmin, setIsAdmin] = useState(null); // Null until fetched
  console.log('App: Rendering main component');
  
  const token = localStorage.getItem('token');
  console.log('App: Token check:', token ? 'Present' : 'Missing');
  
  const path = window.location.pathname.toLowerCase();
  console.log('App: Current path (normalized):', path);

  useEffect(() => {
    const fetchUser = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('App: User data:', response.data);
        setIsAdmin(response.data.is_admin);
      } catch (err) {
        console.error('App: User fetch error:', err);
        setIsAdmin(false); // Default to non-admin on error
      }
    };
    fetchUser();
  }, [token]);

  // Handle root redirect AFTER we know admin status
  useEffect(() => {
    try {
      console.log('App: Processing path:', path);
      if (token && path === '/' && isAdmin !== null) {
        const redirectPath = isAdmin ? '/admin' : '/question-of-the-day';
        console.log('Redirecting from root to:', redirectPath);
        window.location.href = redirectPath;
      }
    } catch (err) {
      setRenderError('Error in path processing: ' + err.message);
      console.error('App: Path error:', err);
    }
  }, [token, path, isAdmin]);

  if (renderError) {
    console.log('App: Render error detected:', renderError);
    return <div className="error">App Error: {renderError}</div>;
  }

  // Public routes (no auth required)
  if (path === '/signup') {
    console.log('App: Matched /signup, rendering Signup');
    return <ErrorBoundary><Signup /></ErrorBoundary>;
  }

  if (path === '/upgrade') {
    console.log('App: Matched /upgrade, rendering Upgrade');
    return <ErrorBoundary><Upgrade /></ErrorBoundary>;
  }

  if (path === '/reset-password') {
    console.log('App: Matched /reset-password, rendering ResetPassword');
    return <ErrorBoundary><ResetPassword /></ErrorBoundary>;
  }

  if (path === '/verify-email') {
    console.log('App: Matched /verify-email, rendering VerifyEmail');
    return <ErrorBoundary><VerifyEmail /></ErrorBoundary>;
  }

  if (path === '/unsubscribe') {
    console.log('App: Matched /unsubscribe, rendering Unsubscribe');
    return <ErrorBoundary><Unsubscribe /></ErrorBoundary>;
  }

  if (path === '/help') {
    console.log('App: Matched /help, rendering Help');
    return <ErrorBoundary><Help /></ErrorBoundary>;
  }

  if (path === '/contact') {
    console.log('App: Matched /contact, rendering Contact');
    return <ErrorBoundary><Contact /></ErrorBoundary>;
  }

  // Require authentication for all other routes
  if (!token) {
    console.log('App: No token, rendering Login');
    return <ErrorBoundary><Login /></ErrorBoundary>;
  }

  if (isAdmin === null) {
    console.log('App: Waiting for admin status...');
    return <div>Loading...</div>;
  }

  // Admin routes
  if (path === '/admin') {
    console.log('App: Rendering Admin at /admin');
    return <ErrorBoundary><Admin /></ErrorBoundary>;
  }

  // Subscriber/User routes
  if (path === '/question-of-the-day') {
    console.log('App: Rendering QuestionOfTheDayMobile (mobile-first)');
    return <ErrorBoundary><QuestionOfTheDayMobile /></ErrorBoundary>;
  }
  
  // Legacy route - keeping old component as backup
  if (path === '/question-of-the-day-old') {
    console.log('App: Rendering QuestionOfTheDay (legacy)');
    return <ErrorBoundary><QuestionOfTheDay /></ErrorBoundary>;
  }

  if (path === '/ai-assistant' || path === '/query') {
    console.log('App: Rendering AI Assistant (Query)');
    return <ErrorBoundary><Query /></ErrorBoundary>;
  }

  if (path === '/view-answers' || path === '/questions') {
    console.log('App: Rendering Questions (View Answers)');
    return <ErrorBoundary><Questions /></ErrorBoundary>;
  }

  if (path === '/search-answers') {
    console.log('App: Rendering SearchAnswers');
    return <ErrorBoundary><SearchAnswers /></ErrorBoundary>;
  }

  if (path === '/add-question') {
    console.log('App: Rendering AddQuestion');
    return <ErrorBoundary><AddQuestion /></ErrorBoundary>;
  }

  if (path === '/account-settings') {
    console.log('App: Rendering AccountSettings');
    return <ErrorBoundary><AccountSettings /></ErrorBoundary>;
  }

  if (path === '/manage-subscription') {
    console.log('App: Rendering ManageSubscription');
    return <ErrorBoundary><ManageSubscription /></ErrorBoundary>;
  }

  // Default routes
  if (path === '/dashboard') {
    console.log('App: Rendering Dashboard');
    return <ErrorBoundary><Dashboard /></ErrorBoundary>;
  }

  // Fallback - redirect based on admin status
  console.log('App: Fallback routing based on admin status', { isAdmin, path });
  return (
    <ErrorBoundary>
      {isAdmin ? <Admin /> : <Dashboard />}
    </ErrorBoundary>
  );
}

export default App;